import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { UsersService } from "../../api/services/users.service";
import { getNotificationRoleSegment } from "../../util/notificationRole";

const initialState = {
  notifications: [],
  unreadCount: 0,
  loading: false,
  error: null,
};

// Async thunk to fetch notifications
export const fetchNotifications = createAsyncThunk(
  "notifications/fetch",
  // async (email, { rejectWithValue }) => {
  async (request, { rejectWithValue }) => {
    try {
      // const response = await UsersService.getNotifications(email);
      const email = typeof request === "string" ? request : request?.email;
      const role =
        typeof request === "string"
          ? ""
          : getNotificationRoleSegment(request?.role);
      const roleId = typeof request === "string" ? "" : request?.roleId;
      const response = await UsersService.getNotifications(email, role, roleId);

      // Process response based on role
      // For DID role: use notifications array directly
      // For Regional Lead: combine notifications with failures (as "Order Creation failed" category)
      // Handle different response structures - could be array or object with notifications property
      const notifications = Array.isArray(response)
        ? response
        : response?.notifications || response?.data?.notifications || [];

      // Only include failure notifications for Regional Lead role
      const isRegionalLead = role === "RegionalLead";

      // Helper function to get the best date value for sorting
      const getNotificationDate = (notification) => {
        const dateStr = notification.lastUpdated || 
                       notification.createdDate || 
                       notification.createdOn ||
                       notification.createdAt ||
                       notification.updatedAt ||
                       notification.timestamp;
        if (!dateStr) return new Date(0); // Default to epoch if no date
        const parsed = new Date(dateStr);
        return isNaN(parsed.getTime()) ? new Date(0) : parsed;
      };

      if (isRegionalLead) {
        const failures = response?.failures || response?.data?.failures || [];

        // Transform failures into notification format for Regional Lead
        const failureNotifications = failures.map((failure, index) => ({
          notificationId:
            failure.notificationId || `failure-${index}-${Date.now()}`,
          heading: failure.category || "Order Creation Failed",
          subHeading:
            failure.subHeading ||
            failure.errorMessage ||
            "Order creation failed",
          category: "Order Creation failed",
          isRead: failure.isRead || false,
          lastUpdated:
            failure.lastUpdated ||
            failure.createdAt ||
            failure.createdOn ||
            null,
          orderId: failure.orderId || null,
          orderGuid: failure.orderGuid || null,
          clientEntity: failure.country || null,
          changes: failure.changes || failure.validationErrors || [],
          createdDate:
            failure.createdOn ||
            failure.createdAt ||
            failure.lastUpdated ||
            null,
          analyser: failure.engagementName || "",
          ...failure,
        }));

        // Combine notifications with failure notifications for Regional Lead and sort by date (latest first)
        const combinedNotifications = [...notifications, ...failureNotifications];
        return combinedNotifications.sort((a, b) => {
          const dateA = getNotificationDate(a);
          const dateB = getNotificationDate(b);
          return dateB - dateA; // Latest first
        });
      }

      // For other roles, return notifications sorted by date (latest first)
      return [...notifications].sort((a, b) => {
        const dateA = getNotificationDate(a);
        const dateB = getNotificationDate(b);
        return dateB - dateA; // Latest first
      });
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch notifications");
    }
  },
);

// Async thunk to mark notifications as read
export const markNotificationsAsRead = createAsyncThunk(
  "notifications/markAsRead",
  async (notificationIds, { rejectWithValue }) => {
    try {
      await UsersService.markNotificationsRead(notificationIds);
      return notificationIds;
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to mark notifications as read",
      );
    }
  },
);

export const markNotificationRead = createAsyncThunk(
  "notification/markRead",
  async (notificationId, { rejectWithValue }) => {
    try {
      await UsersService.markNotificationRead(notificationId);
      return notificationId;
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to mark notifications as read",
      );
    }
  },
);

const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    markAsRead: (state, action) => {
      const notificationId = action.payload;
      const notification = state.notifications.find(
        (n) => n.notificationId === notificationId,
      );
      if (notification && !notification.isRead) {
        notification.isRead = true;
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },
    markAllAsRead: (state) => {
      state.notifications.forEach((notification) => {
        notification.isRead = true;
      });
      state.unreadCount = 0;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;
        // Handle both array and object with data/notifications property
        const payload = action.payload;
        const notificationsData = Array.isArray(payload)
          ? payload
          : payload?.data || payload?.notifications || [];
        state.notifications = notificationsData;
        state.unreadCount = notificationsData.filter((n) => !n.isRead).length;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(markNotificationsAsRead.fulfilled, (state, action) => {
        const notificationIds = action.payload;
        state.notifications.forEach((notification) => {
          if (notificationIds.includes(notification.notificationId)) {
            notification.isRead = true;
          }
        });
        state.unreadCount = state.notifications.filter((n) => !n.isRead).length;
      })
      .addCase(markNotificationRead.fulfilled, (state, action) => {
        const notificationId = action.payload;
        const notification = state.notifications.find(
          (n) => n.notificationId === notificationId,
        );
        if (notification && !notification.isRead) {
          notification.isRead = true;
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      });
  },
});
export const { markAsRead, markAllAsRead, clearError } =
  notificationsSlice.actions;
export default notificationsSlice.reducer;
